import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Trash2 } from 'lucide-react';
import {
  GROUP_CONTENT_RULES,
  GROUP_STICKERS,
  buildContact,
  buildEvent,
  buildLocation,
  buildPoll,
  checkMedia,
  defaultEventStart,
  formatDuration,
  mediaTypeForPick,
  parseCoordinates,
  pollFormRows,
  takesCaption,
  type GroupContent,
  type GroupMediaType,
} from '../../../lib/group-content';
import { sendGroupContent, sendGroupMedia, sendGroupVoiceNote } from '../../../lib/group-content-api';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { Modal } from '../../Modal';
import { Button, IconButton, Input, Switch, TextButton, Typography } from '../ui';
import { StickerArt } from './StickerArt';

/**
 * Group chat Phase 10c — what the group composer's "+" opens on web: the caption step for a picked photo, video, GIF
 * or document, the voice recorder, and the poll, event, place, contact and sticker forms. Every check is
 * `lib/group-content.ts` (the same rules both phones run); each form sends through `lib/group-content-api.ts`.
 */

export type GroupAttachKind = 'media' | 'document' | 'voice' | 'poll' | 'event' | 'location' | 'contact' | 'sticker';

const BI = { page_name: 'chat_home' } as const;

const errorLine = (text: string | null) =>
  text ? (
    <Typography variant="p3" color="inherit" role="alert" style={{ color: 'var(--ds-text-destructive)' }}>
      {text}
    </Typography>
  ) : null;

function toLocalInput(d: Date | null): string {
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromLocalInput = (s: string): Date | null => (s ? new Date(s) : null);

/** Pixel size (and a video's length) of a picked file, read from the browser's own decoders. */
function measure(file: File, type: GroupMediaType): Promise<{ width: number; height: number; durationMs: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const done = (v: { width: number; height: number; durationMs: number }) => {
      URL.revokeObjectURL(url);
      resolve(v);
    };
    if (type === 'IMAGE' || (type === 'GIF' && file.type === 'image/gif')) {
      const img = new Image();
      img.onload = () => done({ width: img.naturalWidth, height: img.naturalHeight, durationMs: 0 });
      img.onerror = () => done({ width: 0, height: 0, durationMs: 0 });
      img.src = url;
      return;
    }
    if (type === 'VIDEO' || type === 'GIF') {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => done({ width: v.videoWidth, height: v.videoHeight, durationMs: Number.isFinite(v.duration) ? Math.round(v.duration * 1000) : 0 });
      v.onerror = () => done({ width: 0, height: 0, durationMs: 0 });
      v.src = url;
      return;
    }
    done({ width: 0, height: 0, durationMs: 0 });
  });
}

/** The caption step for a picked file: a preview, the caption field, Send. Refuses a bad type/size before any call. */
export function GroupMediaSendDialog({ conversationId, file, from, replyToMessageId, onSent, onClose }: { conversationId: string; file: File; from: 'media' | 'document'; replyToMessageId?: string; onSent?: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const type = mediaTypeForPick(file.type, from);
  const check = checkMedia(type, file.type, file.size);
  const [caption, setCaption] = useState('');
  const [preview] = useState(() => (type === 'FILE' ? null : URL.createObjectURL(file)));
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);
  const problem = check.ok ? null : t(check.key, { ...(check.vars ?? {}), ...(check.kindKey ? { kind: t(check.kindKey) } : {}) });
  const closed = useRef(false);
  useEffect(() => () => {
    closed.current = true;
  }, []);
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!check.ok || sending) return;
    setSending(true);
    logPageClickBi({ ...BI, button_name: `group_send_${type.toLowerCase()}` });
    const dims = await measure(file, type);
    // Closed while measuring: the reader cancelled, so nothing is sent.
    if (closed.current) return;
    sendGroupMedia({ conversationId, type, file, fileName: file.name || 'file', mimeType: check.mimeType, caption, ...dims, ...(replyToMessageId ? { replyToMessageId } : {}) });
    onSent?.();
    onClose();
  };
  return (
    <Modal open onClose={onClose} title={file.name || t('chat.group.media.document')} width={480} maxHeightPx={650} scrollBody
      footer={<Button onClick={() => void send()} disabled={!check.ok} loading={sending} data-testid="group-media-send">{t('chat.group.caption.send')}</Button>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {preview && type === 'IMAGE' && (
          <img src={preview} alt="" style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain', alignSelf: 'center' }} />
        )}
        {preview && (type === 'VIDEO' || type === 'GIF') && (file.type.startsWith('video/') ? (
          <video src={preview} controls muted playsInline style={{ maxWidth: '100%', maxHeight: 320 }} />
        ) : (
          <img src={preview} alt="" style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain', alignSelf: 'center' }} />
        ))}
        {errorLine(problem)}
        {takesCaption(type) && (
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={t('chat.group.caption.placeholder')} maxLength={GROUP_CONTENT_RULES.maxCaption} dir="auto" />
        )}
      </div>
    </Modal>
  );
}

// ─── Voice message ───────────────────────────────────────────────────────────────────────────────────────────

/** The recorder panel: start, stop, then send or discard — the same panel the phones' "Voice message" opens. */
export function GroupVoiceDialog({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [phase, setPhase] = useState<'idle' | 'recording' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const samples = useRef<number[]>([]);
  const started = useRef(0);
  const result = useRef<{ blob: Blob; durationMs: number; mime: string } | null>(null);
  const cleanup = useRef<() => void>(() => {});
  useEffect(() => () => cleanup.current(), []);

  const start = async () => {
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError(t('chat.group.voice.micDenied'));
      return;
    }
    const mime = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
    const rec = new MediaRecorder(stream, { mimeType: mime });
    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    samples.current = [];
    chunks.current = [];
    const tick = setInterval(() => {
      analyser.getByteTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
      samples.current.push(peak);
      const ms = Date.now() - started.current;
      setElapsed(ms);
      if (ms >= GROUP_CONTENT_RULES.voiceMaxMs) rec.stop();
    }, 50);
    cleanup.current = () => {
      clearInterval(tick);
      stream.getTracks().forEach((tr) => tr.stop());
      void ctx.close();
    };
    rec.ondataavailable = (e) => chunks.current.push(e.data);
    rec.onstop = () => {
      cleanup.current();
      const durationMs = Date.now() - started.current;
      result.current = { blob: new Blob(chunks.current, { type: mime }), durationMs, mime };
      setPhase('done');
    };
    recorder.current = rec;
    started.current = Date.now();
    rec.start();
    setPhase('recording');
    logPageClickBi({ ...BI, button_name: 'group_voice_record' });
  };
  const send = () => {
    const r = result.current;
    if (!r) return;
    // The same send as the composer's mic (lib/group-content-api.ts `sendGroupVoiceNote`).
    const refused = sendGroupVoiceNote(conversationId, { blob: r.blob, mimeType: r.mime, durationMs: r.durationMs, samples: samples.current });
    if (refused) {
      setError(t(refused.key, refused.vars));
      return;
    }
    logPageClickBi({ ...BI, button_name: 'group_send_voice_note' });
    onClose();
  };
  return (
    <Modal open onClose={onClose} title={t('chat.group.attach.voice')} width={420} maxHeightPx={650} scrollBody
      footer={phase === 'done' ? (
        <>
          <Button variant="outlined" startIcon={<Trash2 size={16} aria-hidden />} onClick={() => { result.current = null; setPhase('idle'); setElapsed(0); }}>{t('chat.group.voice.discard')}</Button>
          <Button onClick={send} data-testid="group-voice-send">{t('chat.group.voice.send')}</Button>
        </>
      ) : undefined}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '12px 0' }}>
        <Typography variant="h3" aria-live="polite">{phase === 'recording' ? t('chat.group.voice.recording', { duration: formatDuration(elapsed) }) : formatDuration(elapsed)}</Typography>
        {phase === 'idle' && <IconButton variant="contained" aria-label={t('chat.group.voice.record')} onClick={() => void start()}><Mic size={20} aria-hidden /></IconButton>}
        {phase === 'recording' && <IconButton variant="contained" color="destructive" aria-label={t('chat.group.voice.stop')} onClick={() => recorder.current?.stop()}><Square size={20} aria-hidden /></IconButton>}
        {errorLine(error)}
      </div>
    </Modal>
  );
}

// ─── Poll ────────────────────────────────────────────────────────────────────────────────────────────────────

export function GroupPollDialog({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [question, setQuestion] = useState('');
  const [rows, setRows] = useState<string[]>(['', '']);
  const [multiple, setMultiple] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = () => {
    const built = buildPoll(question, rows, multiple);
    if (!built.ok) return setError(t(built.key));
    logPageClickBi({ ...BI, button_name: 'group_send_poll' });
    void sendGroupContent(conversationId, { type: 'POLL', caption: '', poll: built.value });
    onClose();
  };
  return (
    <Modal open onClose={onClose} title={t('chat.group.poll.create')} width={460} maxHeightPx={650} scrollBody
      footer={<Button onClick={send} data-testid="group-poll-send">{t('chat.group.poll.send')}</Button>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input label={t('chat.group.poll.question')} placeholder={t('chat.group.poll.questionPlaceholder')} value={question} maxLength={GROUP_CONTENT_RULES.maxQuestion} onChange={(e) => setQuestion(e.target.value)} dir="auto" />
        <Typography variant="p2">{t('chat.group.poll.options')}</Typography>
        {rows.map((row, i) => (
          <Input
            key={i}
            value={row}
            dir="auto"
            maxLength={GROUP_CONTENT_RULES.maxOptionText}
            placeholder={t('chat.group.poll.optionPlaceholder', { number: i + 1 })}
            onChange={(e) => setRows(pollFormRows(rows.map((r, j) => (j === i ? e.target.value : r))))}
            onClear={rows.length > 2 ? () => setRows(pollFormRows(rows.filter((_, j) => j !== i))) : undefined}
            clearAriaLabel={t('chat.group.poll.removeOption')}
          />
        ))}
        <Switch checked={multiple} onChange={setMultiple} label={t('chat.group.poll.multiple')} />
        {errorLine(error)}
      </div>
    </Modal>
  );
}

// ─── Event ───────────────────────────────────────────────────────────────────────────────────────────────────

export function GroupEventDialog({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [title, setTitle] = useState('');
  const [start, setStart] = useState(toLocalInput(defaultEventStart()));
  const [end, setEnd] = useState('');
  const [where, setWhere] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const send = () => {
    const built = buildEvent({ title, start: fromLocalInput(start), end: fromLocalInput(end), location: where, description });
    if (!built.ok) return setError(t(built.key));
    logPageClickBi({ ...BI, button_name: 'group_send_event' });
    void sendGroupContent(conversationId, { type: 'EVENT', caption: '', event: built.value });
    onClose();
  };
  return (
    <Modal open onClose={onClose} title={t('chat.group.eventCard.create')} width={460} maxHeightPx={650} scrollBody
      footer={<Button onClick={send} data-testid="group-event-send">{t('chat.group.eventCard.send')}</Button>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input label={t('chat.group.eventCard.title')} placeholder={t('chat.group.eventCard.titlePlaceholder')} value={title} maxLength={GROUP_CONTENT_RULES.maxEventTitle} onChange={(e) => setTitle(e.target.value)} dir="auto" />
        <Input label={t('chat.group.eventCard.start')} type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        <Input label={t('chat.group.eventCard.end')} type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        <Input label={t('chat.group.eventCard.location')} value={where} maxLength={GROUP_CONTENT_RULES.maxEventLocation} onChange={(e) => setWhere(e.target.value)} dir="auto" />
        <Input label={t('chat.group.eventCard.description')} value={description} maxLength={GROUP_CONTENT_RULES.maxEventDescription} onChange={(e) => setDescription(e.target.value)} dir="auto" />
        {errorLine(error)}
      </div>
    </Modal>
  );
}

// ─── Place ───────────────────────────────────────────────────────────────────────────────────────────────────

export function GroupLocationDialog({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState('');
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sendPoint = (latitude: number, longitude: number, bi: string) => {
    const built = buildLocation({ latitude, longitude, label, address });
    if (!built.ok) return setError(t(built.key));
    logPageClickBi({ ...BI, button_name: bi });
    void sendGroupContent(conversationId, { type: 'LOCATION', caption: '', location: built.value });
    onClose();
  };
  const current = () => {
    if (!navigator.geolocation) return setError(t('chat.group.location.unavailable'));
    setLocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        sendPoint(pos.coords.latitude, pos.coords.longitude, 'group_send_location_current');
      },
      (err) => {
        setLocating(false);
        setError(t(err.code === err.PERMISSION_DENIED ? 'chat.group.location.denied' : 'chat.group.location.unavailable'));
      },
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  };
  const manual = () => {
    const point = parseCoordinates(coordinates);
    if (!point) return setError(t('chat.group.location.invalidCoordinates'));
    sendPoint(point.latitude, point.longitude, 'group_send_location_manual');
  };
  return (
    <Modal open onClose={onClose} title={t('chat.group.location.share')} width={460} maxHeightPx={650} scrollBody
      footer={<Button onClick={manual} data-testid="group-location-send">{t('chat.group.location.send')}</Button>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Button variant="outlined" loading={locating} onClick={current} data-testid="group-location-current">{locating ? t('chat.group.location.locating') : t('chat.group.location.current')}</Button>
        <Input label={t('chat.group.location.label')} value={label} maxLength={GROUP_CONTENT_RULES.maxLocationLabel} onChange={(e) => setLabel(e.target.value)} dir="auto" />
        <Input label={t('chat.group.location.address')} value={address} maxLength={GROUP_CONTENT_RULES.maxLocationAddress} onChange={(e) => setAddress(e.target.value)} dir="auto" />
        <Input label={t('chat.group.location.coordinates')} placeholder={t('chat.group.location.coordinatesPlaceholder')} value={coordinates} onChange={(e) => setCoordinates(e.target.value)} />
        {errorLine(error)}
      </div>
    </Modal>
  );
}

// ─── Contact ─────────────────────────────────────────────────────────────────────────────────────────────────

export function GroupContactDialog({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [name, setName] = useState('');
  const [phones, setPhones] = useState<string[]>(['']);
  const [emails, setEmails] = useState<string[]>(['']);
  const [error, setError] = useState<string | null>(null);
  const send = () => {
    const built = buildContact({ name, phones, emails });
    if (!built.ok) return setError(t(built.key));
    logPageClickBi({ ...BI, button_name: 'group_send_contact' });
    void sendGroupContent(conversationId, { type: 'CONTACT', caption: '', contact: built.value });
    onClose();
  };
  const list = (values: string[], set: (v: string[]) => void, labelKey: string, addKey: string, type: string) => (
    <>
      {values.map((v, i) => (
        <Input key={i} type={type} label={i === 0 ? t(labelKey) : undefined} value={v} onChange={(e) => set(values.map((x, j) => (j === i ? e.target.value : x)))} />
      ))}
      {values.length < GROUP_CONTENT_RULES.maxContactValues && <TextButton size="small" onClick={() => set([...values, ''])}>{t(addKey)}</TextButton>}
    </>
  );
  return (
    <Modal open onClose={onClose} title={t('chat.group.attach.contact')} width={460} maxHeightPx={650} scrollBody
      footer={<Button onClick={send} data-testid="group-contact-send">{t('chat.group.contact.send')}</Button>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input label={t('chat.group.contact.name')} value={name} maxLength={GROUP_CONTENT_RULES.maxContactName} onChange={(e) => setName(e.target.value)} dir="auto" />
        {list(phones, setPhones, 'chat.group.contact.phone', 'chat.group.contact.addPhone', 'tel')}
        {list(emails, setEmails, 'chat.group.contact.email', 'chat.group.contact.addEmail', 'email')}
        {errorLine(error)}
      </div>
    </Modal>
  );
}

// ─── Sticker ─────────────────────────────────────────────────────────────────────────────────────────────────

export function GroupStickerDialog({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const send = (stickerId: string) => {
    logPageClickBi({ ...BI, button_name: 'group_send_sticker' });
    const content: GroupContent = { type: 'STICKER', caption: '', stickerId };
    void sendGroupContent(conversationId, content);
    onClose();
  };
  return (
    <Modal open onClose={onClose} title={t('chat.group.sticker.title')} size="minimum" maxHeightPx={650} scrollBody>
      <div data-testid="group-sticker-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, justifyItems: 'center' }}>
        {GROUP_STICKERS.map((s) => (
          <Button key={s.id} variant="text" color="standard" aria-label={t('chat.group.sticker.a11y', { emoji: s.emoji })} onClick={() => send(s.id)} data-testid={`group-sticker-${s.id}`}>
            <StickerArt stickerId={s.id} size={64} />
          </Button>
        ))}
      </div>
      <Typography variant="p3" color="secondary" style={{ display: 'block', marginTop: 12, textAlign: 'center' }}>
        {t('chat.group.sticker.attribution')}
      </Typography>
    </Modal>
  );
}

